<div class="domain-import-confirm">
    <!-- Import Summary -->
    <div class="import-overview mb-4">
        <div class="row">
            <div class="col-md-32">
                <h5 translate>Import Overview</h5>
                <p class="text-muted" translate>
                    Review the domains and settings before starting the import process.
                </p>
            </div>
            <div class="col-md-16 text-end">
                <div class="import-stats">
                    <div class="stat-item">
                        <span class="stat-number">{{ parsedFile?.totalCount || 0 }}</span>
                        <span class="stat-label" translate>Domains</span>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Configuration Summary -->
    <div class="configuration-summary mb-4">
        <h6 translate>Import Configuration</h6>
        <div class="config-grid" *ngIf="configuration; else configSkeleton">
            <div class="config-item" *ngFor="let key of ['skipDomainGroups', 'skipPathConstraints', 'skipLinkExcludes']">
                <div class="config-icon">
                    <i [class]="getConfigIcon(key)"></i>
                </div>
                <div class="config-details">
                    <span class="config-label">{{ getConfigLabel(key) }}</span>
                    <span class="config-status" [class]="getConfigStatusClass(key)">{{ getConfigStatus(key) }}</span>
                </div>
            </div>
        </div>
        <ng-template #configSkeleton>
            <div class="config-grid">
                <div class="config-item skeleton-item" *ngFor="let item of [1,2,3,4]">
                    <div class="config-icon skeleton-box"></div>
                    <div class="config-details">
                        <div class="skeleton-text skeleton-text-sm"></div>
                        <div class="skeleton-text skeleton-text-xs"></div>
                    </div>
                </div>
            </div>
        </ng-template>
    </div>

    <!-- Domain List -->
    <div class="domain-list-container mb-4">
        <div class="domain-list" *ngIf="parsedFile?.domains?.length; else domainSkeleton">
            <h6 translate>Domains to Import</h6>
            
            <!-- Domain Table -->
            <div class="table-responsive">
                <table class="table table-striped">
                    <thead>
                        <tr>
                            <th translate>Domain</th>
                            <th translate>URL</th>
                            <th translate>Components</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr *ngFor="let domain of paginatedDomains; let i = index">
                            <td>
                                <div class="domain-info">
                                    <strong>{{ domain.title }}</strong>
                                    <div class="domain-index text-muted small">
                                        #{{ ((currentPage - 1) * pageSize) + i + 1 }}
                                    </div>
                                </div>
                            </td>
                            <td>
                                <code class="domain-url">{{ domain.url }}</code>
                            </td>
                            <td>
                                <div class="component-indicators">
                                    <span
                                        class="component-badge"
                                        [class.has-component]="hasDomainComponent(domain, 'groups')"
                                        [class.text-muted]="configuration?.skipDomainGroups"
                                        title="Domain Groups">
                                        <i class="fas fa-layer-group"></i>
                                    </span>
                                    <span
                                        class="component-badge"
                                        [class.has-component]="hasDomainComponent(domain, 'constraints')"
                                        [class.text-muted]="configuration?.skipPathConstraints"
                                        title="Path Constraints">
                                        <i class="fas fa-route"></i>
                                    </span>
                                    <span
                                        class="component-badge"
                                        [class.has-component]="hasDomainComponent(domain, 'excludes')"
                                        [class.text-muted]="configuration?.skipLinkExcludes"
                                        title="Link Excludes">
                                        <i class="fas fa-ban"></i>
                                    </span>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <nav aria-label="Domain pagination" *ngIf="totalPages > 1">
                <ul class="pagination justify-content-center">
                    <li class="page-item" [class.disabled]="currentPage === 1">
                        <button class="page-link" (click)="previousPage()" [disabled]="currentPage === 1">
                            <i class="fas fa-chevron-left"></i>
                            <span translate>Previous</span>
                        </button>
                    </li>
                    
                    <li class="page-item"
                        *ngFor="let page of getPageNumbers()"
                        [class.active]="page === currentPage">
                        <button class="page-link" (click)="goToPage(page)">{{ page }}</button>
                    </li>
                    
                    <li class="page-item" [class.disabled]="currentPage === totalPages">
                        <button class="page-link" (click)="nextPage()" [disabled]="currentPage === totalPages">
                            <span translate>Next</span>
                            <i class="fas fa-chevron-right"></i>
                        </button>
                    </li>
                </ul>
            </nav>
            
            <!-- Pagination Info -->
            <div class="pagination-info text-center text-muted small" *ngIf="totalPages > 1">
                <span translate>
                    Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, parsedFile?.totalCount || 0) }} of {{ parsedFile?.totalCount || 0 }} domains
                </span>
            </div>
        </div>

        <ng-template #domainSkeleton>
            <div class="domain-list">
                <h6 class="skeleton-text skeleton-text-md" style="margin: 0; padding: 1rem 1.5rem; background: #f8f9fa;"></h6>
                <div class="table-responsive">
                    <table class="table table-striped">
                        <thead>
                            <tr>
                                <th class="skeleton-text skeleton-text-sm"></th>
                                <th class="skeleton-text skeleton-text-sm"></th>
                                <th class="skeleton-text skeleton-text-sm"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let item of [1,2,3]">
                                <td>
                                    <div class="skeleton-text skeleton-text-md mb-1"></div>
                                    <div class="skeleton-text skeleton-text-xs"></div>
                                </td>
                                <td>
                                    <div class="skeleton-text skeleton-text-lg"></div>
                                </td>
                                <td>
                                    <div class="component-indicators">
                                        <div class="skeleton-box component-badge"></div>
                                        <div class="skeleton-box component-badge"></div>
                                        <div class="skeleton-box component-badge"></div>
                                        <div class="skeleton-box component-badge"></div>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </ng-template>
    </div>

    <!-- File Info -->
    <div class="file-info" *ngIf="parsedFile; else fileSkeleton">
        <h6 translate>Export File Information</h6>
        <div class="info-grid">
            <div class="info-item" *ngIf="parsedFile.exportedAt">
                <span class="info-label" translate>Exported:</span>
                <span class="info-value">{{ parsedFile.exportedAt | date:'medium' }}</span>
            </div>
            <div class="info-item" *ngIf="parsedFile.exportedBy">
                <span class="info-label" translate>Exported by:</span>
                <span class="info-value">{{ parsedFile.exportedBy }}</span>
            </div>
            <div class="info-item">
                <span class="info-label" translate>Total domains:</span>
                <span class="info-value">{{ parsedFile.totalCount }}</span>
            </div>
        </div>
    </div>

    <ng-template #fileSkeleton>
        <div class="file-info">
            <h6 class="skeleton-text skeleton-text-md mb-3"></h6>
            <div class="info-grid">
                <div class="info-item" *ngFor="let item of [1,2,3]">
                    <span class="skeleton-text skeleton-text-sm"></span>
                    <span class="skeleton-text skeleton-text-md"></span>
                </div>
            </div>
        </div>
    </ng-template>
</div>

